import { useState, useEffect, useRef } from 'react';
import { MdAddBox, MdClose, MdOutlineFavorite } from 'react-icons/md';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';
import { getProducts, getProductsAlreadyOrdered } from 'interfaces/products';

const AddProductsModal = (props: {
    isOpen: any;
    onClose: any;
    addProducts: any;
    existingProducts: any;
    customerId?: string;
}) => {
    const { isOpen, onClose, addProducts, existingProducts, customerId } = props;

    const [search, setSearch] = useState('');
    const [refSearch, setRefSearch] = useState('');
    const [tab, setTab] = useState('all');
    const [customProducts, setCustomProducts] = useState<any[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [alreadyOrderedProducts, setAlreadyOrderedProducts] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedPackagings, setSelectedPackagings] = useState<{[key: string]: any}>({});
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const loadProducts = async (page: number, orderedProducts?: any[]) => {
        if (isLoading) return;
        setIsLoading(true);
        const res = await getProducts(page, 100, customerId);
        if (res && res.data) {
            const recentList = orderedProducts || alreadyOrderedProducts || [];
            const lastOrdersIds = recentList.map((order: any) => order._id);
            
            // Mark products as isRecent without duplicating
            const formattedProducts = res.data.map((product: any) => ({
                ...product,
                isRecent: lastOrdersIds.includes(product._id) && product.name
            }));
            
            if (page === 1) {
                // On the first page, add already ordered products first
                const recentProducts = recentList.map((p: any) => ({
                    ...p,
                    isRecent: true
                }));
                
                // Filter out duplicates from the fetched products
                const allIds = new Set(lastOrdersIds);
                const nonRecentProducts = formattedProducts.filter((p: any) => !allIds.has(p._id));
                
                setProducts([...recentProducts, ...nonRecentProducts]);
            } else {
                setProducts((prev) => [...prev, ...formattedProducts]);
            }
            setHasMore(res.data.length === 100);
        }
        setIsLoading(false);
    };

    const loadAllRemainingProducts = async () => {
        if (isLoading || !hasMore) return;
        setIsLoading(true);
        let page = currentPage + 1;
        let allNewProducts: any[] = [];
        let continueLoading = true;
        
        while (continueLoading) {
            const res = await getProducts(page, 100, customerId);
            if (res && res.data && res.data.length > 0) {
                const formatted = formatProducts(res.data);
                allNewProducts = [...allNewProducts, ...formatted];
                continueLoading = res.data.length === 100;
                page++;
            } else {
                continueLoading = false;
            }
        }
        
        if (allNewProducts.length > 0) {
            // Keep already ordered products first
            const lastOrders = alreadyOrderedProducts || [];
            const lastOrdersIds = new Set(lastOrders.map((order: any) => order._id));
            
            // Filter new products to avoid duplicates with already ordered products
            const nonRecentNewProducts = allNewProducts.filter((p: any) => !lastOrdersIds.has(p._id));
            
            // Rebuild: existing products (already including recent ones first) + new products
            setProducts((prev) => {
                // Split existing products into recent and non-recent
                const existingRecent = prev.filter((p: any) => p.isRecent);
                const existingNonRecent = prev.filter((p: any) => !p.isRecent);
                return [...existingRecent, ...existingNonRecent, ...nonRecentNewProducts];
            });
            setCurrentPage(page - 1);
        }
        setHasMore(false);
        setIsLoading(false);
    };

    const loadAlreadyOrderedProducts = async () => {
        if (!customerId) return [];
        const res = await getProductsAlreadyOrdered(customerId);
        if (res) {
            setAlreadyOrderedProducts(res);
            return res;
        }
        return [];
    };

    const formatProducts = (productsList: any[]) => {
        const lastOrders = alreadyOrderedProducts || [];
        const lastOrdersIds = lastOrders.map((order: any) => order._id);
        
        // Juste marquer les produits comme isRecent, sans les dupliquer
        return productsList.map((product: any) => ({
            ...product,
            isRecent: lastOrdersIds.includes(product._id) && product.name
        }));
    };

    const handleItemClicked = (product: any, quantity: number) => {
        const selectedPackaging = selectedPackagings[product._id];
        
        const newProduct = {
            ...product,
            productId: product._id,
            price: selectedPackaging ? parseFloat(selectedPackaging.price) : parseFloat(product.price),
            unity: selectedPackaging ? selectedPackaging.unity : product.unity,
            vat: parseFloat(product.vat),
            quantity,
            discountType: 'none',
            discountValue: 0,
            // Add packaging info if selected
            ...(selectedPackaging && {
                packagingRef: selectedPackaging.ref,
                packagingUnity: selectedPackaging.unity,
                packagingWeight: selectedPackaging.weight,
            }),
        };
        if (!selectedProducts.find((i) => i.productId === newProduct.productId))
            setSelectedProducts((prev) => [...prev, newProduct]);
        else if (quantity <= 0)
            setSelectedProducts((prev) =>
                prev.filter((i) => i.productId !== newProduct.productId),
            );
        else
            setSelectedProducts((prev) =>
                prev.map((i) =>
                    i.productId === newProduct.productId
                        ? { ...i, quantity, price: newProduct.price, unity: newProduct.unity, packagingRef: newProduct.packagingRef, packagingUnity: newProduct.packagingUnity, packagingWeight: newProduct.packagingWeight }
                        : i,
                ),
            );
    };

    const searchProduct = (product: any, search: string, refSearch: string) => {

        const name = (product.name || '').toString().toLowerCase();
        const ref = (product.ref || '').toString().toLowerCase();
        const searchTerm = (search || '').trim().toLowerCase();
        const refTerm = (refSearch || '').trim().toLowerCase();

        if (!search && !refSearch) return true;

        if (refTerm) {
            if (!ref.includes(refTerm)) return false;
            // if ref matches and no name search, accept
            if (!searchTerm) return true;
        }

        // if name search only (or both)
        // split into terms and match fuzzy
        const terms = searchTerm.split(/\s+/).filter(Boolean);
        if (terms.length === 0) return true; // nothing meaningful to match

        const escapeRegex = (text: string) =>
            text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fuzzify = (term: string): RegExp => {
            const escaped = escapeRegex(term);
            const length = term.length;

        if (length <= 0) {
            return new RegExp(`\\b${escaped}\\b`, 'i');
        }

        if (length <= 4) {
            const loose = escaped.split('').join('.{0,2}');
            return new RegExp(loose, 'i');
        }

        const fuzzy = escaped.split('').join('.*');
        return new RegExp(fuzzy, 'i');
    };

    return terms.every((term) => {
        const regex = fuzzify(term);
        return regex.test(name);
    });
};

    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            function (c) {
                var r = (Math.random() * 16) | 0,
                    v = c == 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            },
        );
    };

    useEffect(() => {
        if (isOpen) {
            setProducts([]);
            setCurrentPage(1);
            setHasMore(true);
            setSelectedPackagings({});
            // Load already ordered products first then the others
            loadAlreadyOrderedProducts().then((orderedProducts) => {
                loadProducts(1, orderedProducts);
            });
        }
    }, [isOpen]);

    useEffect(() => {

        const container = scrollContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            if (isLoading || !hasMore) return;
            
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight - 200) {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                loadProducts(nextPage);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [currentPage, isLoading, hasMore]);

    // Load all remaining products when a search is active
    useEffect(() => {
        const hasSearch = (search && search.length >= 2) || (refSearch && refSearch.length >= 2);
        if (hasSearch && hasMore && !isLoading) {
            loadAllRemainingProducts();
        }
    }, [search, refSearch]);

    const handleCustomerProductChange = (
        index: number,
        key: string,
        value: any,
    ) => {
        setCustomProducts(
            customProducts.map((item, i) =>
                i === index
                    ? {
                          ...item,
                          [key]: value,
                      }
                    : item,
            ),
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="relative !z-50 mx-auto md:top-[7vh]">
                <ModalBody>
                    <Card extra="mt-5 lg:mt-0 px-[10px] lg:px-[30px] pt-[35px] pb-[40px] max-w-[95%] lg:max-w-[70%] w-full mx-auto flex flex-col" style={{ maxHeight: '600px' }}>
                        <div className="absolute top-4 right-4">
                            <button
                                className="text-red-500 hover:text-red-600 hover:cursor-pointer"
                                onClick={onClose}
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                        <h1 className="text-lg font-bold">
                            Ajouter des produits
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Cliquez sur les produits que vous souhaitez ajouter
                            à la commande
                        </p>
                        <div className="grid gap-1 grid-cols-1 lg:grid-cols-12">
                            <div className="col-span-3">
                                <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par référence"
                                        onChange={(e) =>
                                            setRefSearch(e.target.value)
                                        }
                                        className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="col-span-9">
                                <div className="mt-3 flex h-[35px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600">
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom produit"
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex mt-3 ml-4 gap-4">
                            <p
                                className={`text-xs ${
                                    tab === 'all'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('all')}
                            >
                                Tout afficher
                            </p>
                            <p
                                className={`text-xs ${
                                    tab === 'recent-order'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('recent-order')}
                            >
                                Déjà commandé
                            </p>
                            <p
                                className={`text-xs ${
                                    tab === 'custom-price'
                                        ? 'text-toola-500 underline'
                                        : 'text-toola-500/80'
                                } hover:underline hover:cursor-pointer`}
                                onClick={() => setTab('custom-price')}
                            >
                                Avec prix personnalisés
                            </p>
                        </div>
                        <div ref={scrollContainerRef} className="mx-1 mt-3 grid grid-cols-1 gap-3 overflow-y-auto flex-1">
                            <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                                <thead className="text-gray-700 bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-2 py-3">
                                            Produit
                                        </th>
                                        <th scope="col" className="px-2 py-3">
                                            Conditionnement
                                        </th>
                                        <th scope="col" className="px-2 py-3">
                                            Prix HT
                                        </th>
                                        <th scope="col" className="px-2 py-3">
                                            Stock
                                        </th>
                                        <th scope="col" className="px-2 py-3">
                                            Quantité
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customProducts.map((item, index) => (
                                        <tr
                                            className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                            key={`custom-${item._id}`}
                                            onClick={() => {}}
                                        >
                                            <td className="px-2 py-2">
                                                {/* Created 10 minutes ago ? Live ping */}

                                                <p className="flex my-auto gap-5">
                                                    <input
                                                        type="text"
                                                        className="px-2 w-[200px] h-[30px] border border-gray-200 rounded-xl"
                                                        placeholder="Nom du produit/service"
                                                        value={item.name}
                                                        onChange={(e) =>
                                                            handleCustomerProductChange(
                                                                index,
                                                                'name',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                    <div className="flex">
                                                        <input
                                                            type="number"
                                                            placeholder="TVA (%)"
                                                            className="px-2 text-xs w-[75px] h-[30px] border border-gray-200 rounded-xl"
                                                            value={item.vat}
                                                            onChange={(e) =>
                                                                handleCustomerProductChange(
                                                                    index,
                                                                    'vat',
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </p>
                                                <div className="flex my-auto gap-2">
                                                    {item?.isRecent && (
                                                        <p className="flex gap-1 my-auto text-sm text-pink-500">
                                                            <MdOutlineFavorite />
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-2 py-3">
                                                -
                                            </td>
                                            <td className="px-2 py-3 gap-2">
                                                <input
                                                    type="number"
                                                    className="px-2 w-[70px] h-[30px] border border-gray-200 rounded-xl"
                                                    placeholder="0"
                                                    value={item.price}
                                                    onChange={(e) =>
                                                        handleCustomerProductChange(
                                                            index,
                                                            'price',
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                                €
                                            </td>
                                            <td className="px-2 py-3">
                                                -
                                            </td>
                                            <td className="px-2 py-3">
                                                -
                                            </td>
                                            <td className="px-2 py-3">
                                                <input
                                                    type="number"
                                                    className="px-2 w-[80px] h-[30px] border border-gray-200 rounded-xl"
                                                    placeholder="0"
                                                    onChange={(e) =>
                                                        handleItemClicked(
                                                            item,
                                                            parseInt(
                                                                e.target.value,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    {products &&
                                        products
                                            .filter((i: any) =>
                                                searchProduct(
                                                    i,
                                                    search,
                                                    refSearch,
                                                ),
                                            )
                                            .filter((i: any) =>
                                                tab === 'recent-order'
                                                    ? i.isRecent
                                                    : tab === 'custom-price'
                                                      ? i.customPrice
                                                      : i,
                                            )
                                            .filter(
                                                (i: any) =>
                                                    !existingProducts.includes(
                                                        i._id,
                                                    ),
                                            ).length === 0 && (
                                            <tr
                                                className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                                key="no-product"
                                            >
                                                <td className="px-2 py-2">
                                                    Aucun produit trouvé
                                                </td>
                                                <td className="px-2 py-3"></td>
                                                <td className="px-2 py-3"></td>
                                                <td className="px-2 py-3"></td>
                                                <td className="px-2 py-3"></td>
                                            </tr>
                                        )}
                                    {products &&
                                        products
                                            .filter((i: any) => !i._id?.toString().startsWith('custom-'))
                                            .filter((i: any) =>
                                                searchProduct(
                                                    i,
                                                    search,
                                                    refSearch,
                                                ),
                                            )
                                            .filter((i: any) =>
                                                tab === 'recent-order'
                                                    ? i.isRecent
                                                    : tab === 'custom-price'
                                                      ? i.customPrice
                                                      : i,
                                            )
                                            
                                            .filter(
                                                (i: any) =>
                                                    !existingProducts.includes(
                                                        i._id,
                                                    ),
                                            )
                                            .map((item: any, index: number) => (
                                                <tr
                                                    className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => {}}
                                                    key={`product-${item._id}`}
                                                >
                                                    <td className="px-2">
                                                        {/* Created 10 minutes ago ? Live ping */}

                                                        <div className="flex my-auto gap-2">
                                                            <p>
                                                                <p className="text-sm">
                                                                    #{item.ref}
                                                                </p>
                                                            </p>
                                                            {' - '}
                                                            <p className="text-sm flex my-auto text-gray-700">
                                                                {item.name}
                                                            </p>
                                                            {item?.isRecent && (
                                                                <p className="flex gap-1 my-auto text-sm text-pink-500">
                                                                    <MdOutlineFavorite />
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-3">
                                                        {item.packagings && item.packagings.length > 0 ? (
                                                            <select
                                                                className="px-2 py-1 text-xs border border-gray-200 rounded-xl bg-white"
                                                                value={selectedPackagings[item._id]?.unity || ''}
                                                                onChange={(e) => {
                                                                    const packaging = item.packagings.find(
                                                                        (p: any) => p.unity === e.target.value
                                                                    );
                                                                    setSelectedPackagings((prev) => ({
                                                                        ...prev,
                                                                        [item._id]: packaging || null
                                                                    }));
                                                                }}
                                                            >
                                                                <option value="">
                                                                    {item.unity} - {item.price}€
                                                                </option>
                                                                {item.packagings.map((pkg: any, idx: number) => (
                                                                    <option key={idx} value={pkg.unity}>
                                                                        {pkg.unity} ({pkg.weight} {item.unity}) - {pkg.price}€
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <span className="text-xs text-gray-500">
                                                                {item.unity}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-3">
                                                        {selectedPackagings[item._id]?.price || item?.price} €
                                                    </td>
                                                    <td className="px-2 py-3">
                                                        <span className={`text-sm ${item?.quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                            {(item?.quantity || 0).toFixed(2)} {item?.unity || ''}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-3">
                                                        <input
                                                            type="number"
                                                            className="px-2 w-[80px] h-[30px] border border-gray-200 rounded-xl"
                                                            placeholder="0"
                                                            value={
                                                                selectedProducts.find(
                                                                    (i) =>
                                                                        i.productId ===
                                                                        item._id,
                                                                )?.quantity
                                                            }
                                                            onChange={(e) =>
                                                                handleItemClicked(
                                                                    item,
                                                                    parseInt(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                </tbody>
                            </table>
                            {isLoading && (
                                <div className="py-4 text-center text-sm text-gray-500">
                                    Chargement...
                                </div>
                            )}
                        </div>
                        <div className="mt-3 flex gap-1">
                            <MdAddBox
                                size={15}
                                className="flex my-auto text-toola-500"
                            />
                            <p
                                className="flex text-sm my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                onClick={() => {
                                    setCustomProducts((prev) => [
                                        ...prev,
                                        {
                                            name: '',
                                            price: 0,
                                            _id: `custom-${generateUUID()}`,
                                        },
                                    ]);
                                }}
                            >
                                Ajouter une ligne personnalisée
                            </p>
                        </div>
                        <div className="mt-5 flex gap-2">
                            <button
                                className="linear col-span-12 ml-3 flex items-center justify-center rounded-xl bg-[#0260CB] bg-[] px-3 py-3 text-xs font-medium text-white hover:bg-[#01499b] active:bg-[#0260CB] sm:col-span-6 lg:col-span-4 xl:col-span-3 xl:col-start-12"
                                onClick={() => {
                                    addProducts(selectedProducts);
                                    setSelectedProducts([]);
                                    setCustomProducts([]);
                                }}
                            >
                                Ajouter
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default AddProductsModal;

